import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import { Modal, Progress } from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  selectProjectList,
  selectCurrentProject,
  setCurrentProject,
} from "../../../store/slices/projects";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import "./project-picker.css";
import ProjectCreateModal from "./project-create-modal";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import ProjectProvisioningModal from "./project-provisioning-modal";
import { refreshProjects } from "../../../store/thunks/projects";
import ApiProjects from "../../../api/projects";
import useAdminEvents from "../../../hooks/useAdminEvents";

export const ProjectPickerModal = memo(({ open, onClose, position }) => {
  const dispatch = useDispatch();
  const projects = useSelector(selectProjectList) || [];
  const current = useSelector(selectCurrentProject);
  const navigate = useProjectNavigate();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [provOpen, setProvOpen] = useState(false);
  const [provProject, setProvProject] = useState(null);

  // const handleCancelProvision = useCallback(async (projectId) => {
  //   await ApiProjects.cancelAndDeleteProjectProvision(projectId);

  //   setProvOpen(false);
  //   setProvProject(null);

  //   await dispatch(refreshProjects());
  // }, [dispatch]);

  const hasProvisioning = useMemo(
    () => projects.some(p => ["provisioning", "canceling"].includes(p?.provisionStatus)),
    [projects]
  );

  useEffect(() => {
    if (!open || !hasProvisioning) return;

    dispatch(refreshProjects());

    const id = setInterval(() => {
      dispatch(refreshProjects());
    }, 5_000);

    return () => clearInterval(id);
  }, [open, hasProvisioning, dispatch]);

  useAdminEvents({
    onProjectChanged: () => {
      dispatch(refreshProjects());
    },
  });

  useEffect(() => {
    if (!provOpen || !provProject?.id) return;

    const fresh = projects.find(p => p.id === provProject.id);
    if (!fresh) return;

    if (!["provisioning", "canceling"].includes(fresh.provisionStatus)) {
      setProvOpen(false);
      setProvProject(null);
    } else {
      setProvProject(fresh);
    }
  }, [projects, provOpen, provProject?.id]);

  const handleSelect = useCallback(
    (p) => {
      dispatch(setCurrentProject(p));
      onClose?.();
      navigate("/dashboard", { projectCode: p.code });
    },
    [dispatch, onClose, navigate]
  );

  const handleRowClick = useCallback(
    (p) => {
      if (["provisioning", "canceling"].includes(p?.provisionStatus)) {
        setProvProject(p);
        setProvOpen(true);
        return;
      }

      handleSelect(p);
    },
    [handleSelect]
  );

  const handleCancelProvision = useCallback(async (projectId) => {
    await ApiProjects.cancelProjectProvision(projectId);

    setProvOpen(false);
    setProvProject(null);

    await dispatch(refreshProjects());
  }, [dispatch]);


  // const handleCancelProvision = useCallback(
  //   async (projectId) => {
  //     await ApiProjects.cancelProjectProvision(projectId);
  //     message.success("Project cloning cancellation requested");
  //     await dispatch(refreshProjects());
  //   },
  //   [dispatch]
  // );

  const handleCreateNew = useCallback(() => setIsCreateOpen(true), []);

  const handleCloseCreate = useCallback(() => {
    setIsCreateOpen(false);
    onClose?.();
  }, [onClose]);

  const closeProv = useCallback(() => {
    setProvOpen(false);
    setProvProject(null);
  }, []);

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={224}
        closable={false}
        className="pp-modal"
        destroyOnHidden
        mask={false}
        style={{
          position: "absolute",
          left: position?.x ?? "70px",
          top: position?.y ?? "80px",
          padding: 0,
          zIndex: 20,
        }}
        getContainer={false}
      >
        <div className="pp-header">All projects</div>
        <div className="pp-divider" />

        <div className="pp-list">
          {projects.map((p) => {
            const isActive = current?.id === p.id;
            const isProvisioning = ["provisioning", "canceling"].includes(p?.provisionStatus);
            const isFailed = p?.provisionStatus === "failed";

            return (
              <button
                key={p.id}
                className={[
                  "pp-row",
                  isActive ? "pp-row--active" : "",
                  isProvisioning ? "pp-row--disabled" : "",
                  isFailed ? "pp-row--failed" : "",
                ].join(" ")}
                onClick={() => handleRowClick(p)}
                type="button"
                aria-pressed={isActive}
                title={p.title}
                data-testid={`project-option-${p.code}`}
              >
                <div className="pp-row__title">{p.title}</div>

                {isProvisioning && (
                  <Progress
                    className="pp-prov__progress pp-prov__progress--smaller"
                    type="circle"
                    percent={Math.min(p.provisionProgress ?? 0, 100)}
                    size={16}
                    strokeWidth={20}
                    strokeColor={{ "0%": "#00000010", "100%": "#00000033" }}
                    railColor="#00000010"
                    showInfo={false}
                  />
                )}

                {isFailed && <span className="pp-row__failed">Failed</span>}

                {isActive && !isProvisioning && (
                  <CheckOutlined className="pp-row__check" />
                )}
              </button>
            );
          })}
        </div>

        <button className="pp-create" type="button" onClick={handleCreateNew}>
          <PlusOutlined className="pp-create__icon" />
          <span>Create new project</span>
        </button>
      </Modal>

      <ProjectCreateModal open={isCreateOpen} onClose={handleCloseCreate} />

      <ProjectProvisioningModal
        open={provOpen}
        project={provProject}
        onClose={closeProv}
        onCancelProvision={handleCancelProvision}
      />
    </>
  );
});

export default ProjectPickerModal;
